import React from 'react'
import { observer } from 'mobx-react-lite'
import { useStores } from '../../cls/stores/MobXProvider'
import OptionsSelector from './OptionsSelector'
import { useSmartTranslation } from '../../cls/TranslationContext'
import { formatString } from '../../config/utils'
// Category options: 1 Marketing, 2 Programming, 3 Web Design, 4 Graphic Design, 5 Content Writing
/**
 * AllowedCategories component provides a dropdown selector of categories
 * that the user is permitted to access. It reads the `userAllowedCategories`
 * map from the MobX store, constructs translation keys from each category’s
 * raw name, and renders an OptionsSelector with translated labels.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.optionStart] – Optional option object ({ key, label, value }) to prepend to the list.
 * @param {string|number} [props.value] – The currently selected category value.
 * @param {...any} rest – Any additional props to pass through to the OptionsSelector.
 * @returns {JSX.Element}
 */
const AllowedCategories = observer(({ ...props }) => {
	const { userAllowedCategories, categories } = useStores()
	const { t, i18n } = useSmartTranslation()
	const curentLanguage = i18n.language

	const { optionStart, value, ...rest } = props
	const options = Object.keys(userAllowedCategories).map((key) => {
		const label = userAllowedCategories[key][curentLanguage]
		if (!label) {
			console.warn(
				`No translation found for category ${key} in language ${curentLanguage}`
			)
			return null
		}
		return {
			key: `by-category-${key}`,
			label: label,
			value: key?.toString()
		}
	})

	// Add the option to start with
	if (optionStart) {
		options.unshift(optionStart)
	}

	// verify if value exists in options[key=value], if not we have to get it from categories
	if (
		value &&
		!Object.keys(userAllowedCategories).includes(value.toString())
	) {
		const category = categories[value]
		if (category) {
			options.unshift({
				key: `by-category-${value}`,
				label: category[curentLanguage],
				value: value.toString()
			})
		} else {
			console.warn(
				`Category with ID ${value} not found in categories store`,
				categories,
				userAllowedCategories
			)
		}
	}

	// trigger onChange and set the value to the first option if no value is selected
	if (!value && options.length > 0) {
		rest.onChange?.(options[0].value)
	}

	return (
		<OptionsSelector
			className="we-allowed-categories"
			placeholder={formatString(t('__select.please%single.category'))}
			options={options}
			value={value?.toString()}
			{...rest}
		/>
	)
})

export default AllowedCategories
